import { useState, useEffect, useCallback } from 'react';
import timeTrackingService from '../services/TimeTrackingService';

export const useTimeTracking = (planId) => {
  const [isTracking, setIsTracking] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [formattedTime, setFormattedTime] = useState('00:00:00');

  // Update state from service
  const updateState = useCallback(() => {
    setIsTracking(timeTrackingService.getIsTracking() && timeTrackingService.getCurrentPlanId() === planId);
    setElapsedTime(timeTrackingService.getElapsedTime());
    setFormattedTime(timeTrackingService.getFormattedElapsed());
  }, [planId]);

  // Event handlers
  const handleTick = useCallback((data) => {
    if (timeTrackingService.getCurrentPlanId() === planId) {
      setElapsedTime(data.elapsed);
      setFormattedTime(timeTrackingService.getFormattedElapsed());
    }
  }, [planId]);

  const handleStarted = useCallback((data) => {
    if (data.planId === planId) {
      setIsTracking(true);
    }
  }, [planId]);

  const handlePaused = useCallback((data) => {
    if (data.planId === planId) {
      setIsTracking(false);
    }
  }, [planId]);

  const handleResumed = useCallback((data) => {
    if (data.planId === planId) {
      setIsTracking(true);
    }
  }, [planId]);

  const handleStopped = useCallback((data) => {
    if (data.planId === planId) {
      setIsTracking(false);
      setElapsedTime(0);
      setFormattedTime('00:00:00');
    }
  }, [planId]);

  // Setup event listeners
  useEffect(() => {
    updateState();

    timeTrackingService.on('tick', handleTick);
    timeTrackingService.on('started', handleStarted);
    timeTrackingService.on('paused', handlePaused);
    timeTrackingService.on('resumed', handleResumed);
    timeTrackingService.on('stopped', handleStopped);

    return () => {
      timeTrackingService.off('tick', handleTick);
      timeTrackingService.off('started', handleStarted);
      timeTrackingService.off('paused', handlePaused);
      timeTrackingService.off('resumed', handleResumed);
      timeTrackingService.off('stopped', handleStopped);
    };
  }, [planId, updateState, handleTick, handleStarted, handlePaused, handleResumed, handleStopped]);

  // Control functions
  const startTracking = useCallback(() => {
    timeTrackingService.startTracking(planId);
  }, [planId]);

  const pauseTracking = useCallback(() => {
    timeTrackingService.pauseTracking();
  }, []);

  const resumeTracking = useCallback(() => {
    timeTrackingService.resumeTracking();
  }, []);

  const stopTracking = useCallback(() => {
    timeTrackingService.stopTracking();
  }, []);

  const toggleTracking = useCallback(() => {
    if (isTracking) {
      pauseTracking();
    } else if (timeTrackingService.getCurrentPlanId() === planId) {
      resumeTracking();
    } else {
      startTracking();
    }
  }, [isTracking, planId, startTracking, pauseTracking, resumeTracking]);

  return {
    isTracking,
    elapsedTime,
    formattedTime,
    startTracking,
    pauseTracking,
    resumeTracking,
    stopTracking,
    toggleTracking,
    isCurrentPlan: timeTrackingService.getCurrentPlanId() === planId
  };
};
